import React, { useRef, useState, useEffect, ReactNode } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  ViewStyle,
  StyleProp,
} from 'react-native';
import { Svg, Path } from 'react-native-svg';

type AccordionProps = {
  title: string;
  open: boolean;
  onToggle: () => void;
  children?: ReactNode;
  content?: string;
  style?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<any>;
  bodyStyle?: StyleProp<ViewStyle>;
  chevronColor?: string;
};

export default function Accordion({
  title,
  open,
  onToggle,
  children,
  content,
  style,
  headerStyle,
  titleStyle,
  bodyStyle,
  chevronColor = '#151515',
}: AccordionProps) {
  const [measuredH, setMeasuredH] = useState(0);
  const animHeight = useRef(new Animated.Value(0)).current;
  const animOpacity = useRef(new Animated.Value(0)).current;
  const rotation = useRef(new Animated.Value(open ? 1 : 0)).current;

  useEffect(() => {
    if (measuredH === 0) return;
    Animated.timing(rotation, {
      toValue: open ? 1 : 0,
      duration: 220,
      useNativeDriver: true,
    }).start();
    Animated.timing(animHeight, {
      toValue: open ? measuredH : 0,
      duration: 260,
      useNativeDriver: false,
    }).start();
    Animated.timing(animOpacity, {
      toValue: open ? 1 : 0,
      duration: open ? 260 : 160,
      useNativeDriver: false,
    }).start();
  }, [open, measuredH]);

  const arrowRotate = rotation.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  const body = children ?? (content ? <Text style={styles.defaultContent}>{content}</Text> : null);

  return (
    <View style={[styles.card, style]}>
      {/* Ghost view to measure natural height of the body */}
      <View
        style={{ position: 'absolute', opacity: 0, left: 0, right: 0 }}
        pointerEvents="none"
        onLayout={(e) => {
          const h = e.nativeEvent.layout.height;
          if (h > 0 && h !== measuredH) setMeasuredH(h);
        }}
      >
        {body}
      </View>

      <TouchableOpacity
        style={[styles.header, headerStyle]}
        onPress={onToggle}
        activeOpacity={0.8}
      >
        <Text style={[styles.title, titleStyle]}>{title}</Text>
        <Animated.View style={{ transform: [{ rotate: arrowRotate }] }}>
          <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
            <Path
              d="M12.8333 5.66699L8.16667 10.3337L3.5 5.66699"
              stroke={chevronColor}
              strokeWidth={2}
              strokeLinecap="square"
            />
          </Svg>
        </Animated.View>
      </TouchableOpacity>

      <Animated.View
        style={[
          { height: animHeight, opacity: animOpacity, overflow: 'hidden' },
          bodyStyle,
        ]}
      >
        {body}
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'Sentient-Medium',
    fontSize: 18,
    color: '#151515',
    flex: 1,
  },
  defaultContent: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 22,
    color: '#3D3D3D',
    paddingTop: 12,
  },
});
